// Tabbio Developer Platform contracts: Sign in with Tabbio (OAuth 2.0) and the
// partner API. Shared by apps/api (server), apps/mobile (consent + settings UI),
// and apps/developer (portal). See docs/planning/developer-platform-plan.md.

/** Scopes a third party app can request from a user on the consent screen. */
export const USER_SCOPES = ['profile', 'email', 'cv:read', 'cv:contact'] as const;
export type UserScope = (typeof USER_SCOPES)[number];

/** Scopes an API key can hold for the partner (company) API. */
export const COMPANY_SCOPES = [
  'companies:read',
  'companies:write',
  'jobs:read',
  'jobs:write',
  'candidates:read',
] as const;
export type CompanyScope = (typeof COMPANY_SCOPES)[number];

export type DeveloperScope = UserScope | CompanyScope;

/**
 * Scopes that cannot be unchecked on the consent screen when the app requests
 * them: without identity there is nothing to sign in with.
 */
export const REQUIRED_USER_SCOPES: readonly UserScope[] = ['profile', 'email'];

/** Human copy for each scope (English source; the app translates via i18n). */
export interface ScopeDescriptor {
  scope: DeveloperScope;
  title: string;
  description: string;
}

export const USER_SCOPE_CATALOG: readonly ScopeDescriptor[] = [
  {
    scope: 'profile',
    title: 'Name, username and profile photo',
    description:
      'Your name, username, headline, language and public profile link. The photo is only shared if you allow it.',
  },
  {
    scope: 'email',
    title: 'Email address',
    description: 'The verified email address on your Tabbio account.',
  },
  {
    scope: 'cv:read',
    title: 'Your CV',
    description:
      'The CV you choose to share, kept up to date automatically, as structured data and as a PDF.',
  },
  {
    scope: 'cv:contact',
    title: 'Private contact details',
    description:
      'Phone number, full address, date of birth, nationality and similar personal details from your CV.',
  },
];

export const COMPANY_SCOPE_CATALOG: readonly ScopeDescriptor[] = [
  {
    scope: 'companies:read',
    title: 'Read companies',
    description: 'List and read the companies your app manages on Tabbio.',
  },
  {
    scope: 'companies:write',
    title: 'Create and update companies',
    description: 'Create company pages on Tabbio and update their details.',
  },
  {
    scope: 'jobs:read',
    title: 'Read jobs',
    description: 'List and read jobs posted by companies your app manages.',
  },
  {
    scope: 'jobs:write',
    title: 'Post and manage jobs',
    description: 'Create, publish, update, close and archive jobs.',
  },
  {
    scope: 'candidates:read',
    title: 'Read candidates',
    description:
      'List applicants for your jobs and read their application details and CV.',
  },
];

/** Consent presets offered on the "Sign in with Tabbio" screen. */
export const CONSENT_PRESETS = ['signin', 'cv', 'full'] as const;
export type ConsentPreset = (typeof CONSENT_PRESETS)[number];

/** Scopes each preset grants, intersected with what the app requested. */
export const CONSENT_PRESET_SCOPES: Record<ConsentPreset, readonly UserScope[]> = {
  signin: ['profile', 'email'],
  cv: ['profile', 'email', 'cv:read'],
  full: ['profile', 'email', 'cv:read', 'cv:contact'],
};

/** Private CV/profile fields that require the `cv:contact` scope. */
export const CV_CONTACT_FIELDS = [
  'phone',
  'addressLine',
  'postalCode',
  'dateOfBirth',
  'nationality',
  'maritalStatus',
  'gender',
  'visaStatus',
  'drivingLicense',
  'idNumber',
] as const;
export type CvContactField = (typeof CV_CONTACT_FIELDS)[number];

/** Prefixes make every credential recognisable by secret scanners. */
export const DEVELOPER_TOKEN_PREFIXES = {
  clientId: 'tbo_ci_',
  clientSecret: 'tbo_cs_',
  apiKey: 'tbo_sk_',
  authorizationCode: 'tbo_ac_',
  accessToken: 'tbo_at_',
  refreshToken: 'tbo_rt_',
  cvLink: 'tbo_cvl_',
} as const;
export type DeveloperTokenKind = keyof typeof DEVELOPER_TOKEN_PREFIXES;

export const DEVELOPER_CLIENT_TYPES = ['confidential', 'public'] as const;
export type DeveloperClientType = (typeof DEVELOPER_CLIENT_TYPES)[number];

export const DEVELOPER_APP_STATUSES = ['active', 'suspended'] as const;
export type DeveloperAppStatus = (typeof DEVELOPER_APP_STATUSES)[number];

export const DEVELOPER_CONNECTION_STATUSES = ['active', 'revoked'] as const;
export type DeveloperConnectionStatus =
  (typeof DEVELOPER_CONNECTION_STATUSES)[number];

/** Token lifetimes (seconds). */
export const DEVELOPER_TOKEN_TTL_SECONDS = {
  authorizationCode: 10 * 60,
  accessToken: 60 * 60,
  refreshToken: 90 * 24 * 60 * 60,
} as const;

export const DEVELOPER_APP_LIMIT_PER_USER = 10;

// ---------------------------------------------------------------------------
// First-party DTOs (app JWT endpoints under /api/developer and /api/connected-apps)
// ---------------------------------------------------------------------------

export interface DeveloperAppPublicInfo {
  id: string;
  name: string;
  logoUrl: string | null;
  homepageUrl: string | null;
  privacyPolicyUrl: string | null;
  termsUrl: string | null;
  verified: boolean;
}

export interface DeveloperAppSummary {
  id: string;
  name: string;
  description: string | null;
  logoUrl: string | null;
  clientId: string;
  clientType: DeveloperClientType;
  status: DeveloperAppStatus;
  redirectUris: string[];
  allowedScopes: UserScope[];
  connectionCount: number;
  apiKeyCount: number;
  /** Requests in the trailing 7 days across every credential of the app. */
  requests7d: number;
  lastRequestAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface DeveloperAppSecretSummary {
  id: string;
  prefix: string;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface DeveloperApiKeySummary {
  id: string;
  name: string;
  prefix: string;
  scopes: CompanyScope[];
  lastUsedAt: string | null;
  expiresAt: string | null;
  createdAt: string;
}

export interface DeveloperManagedCompanySummary {
  id: string;
  name: string;
  slug: string;
  externalId: string | null;
  createdAt: string;
}

export interface DeveloperAppDetail extends DeveloperAppSummary {
  homepageUrl: string | null;
  privacyPolicyUrl: string | null;
  termsUrl: string | null;
  secrets: DeveloperAppSecretSummary[];
  apiKeys: DeveloperApiKeySummary[];
  managedCompanies: DeveloperManagedCompanySummary[];
}

export interface CreateDeveloperAppInput {
  name: string;
  description?: string | null;
  logoUrl?: string | null;
  homepageUrl?: string | null;
  privacyPolicyUrl?: string | null;
  termsUrl?: string | null;
  clientType: DeveloperClientType;
  redirectUris: string[];
  allowedScopes: UserScope[];
}

export type UpdateDeveloperAppInput = Partial<
  Omit<CreateDeveloperAppInput, 'clientType'>
>;

export interface CreateDeveloperApiKeyInput {
  name: string;
  scopes: CompanyScope[];
  expiresAt?: string | null;
}

export interface DeveloperCatalog {
  userScopes: ScopeDescriptor[];
  companyScopes: ScopeDescriptor[];
  docsUrl: string;
  platformUrl: string;
  authorizeUrl: string;
  tokenUrl: string;
  apiBaseUrl: string;
}

/**
 * Whether the CV a connection shares can still be read. `'unavailable'` means
 * the user pinned one specific CV for this app and then deleted it: the app is
 * refused (`CV_SELECTION_UNAVAILABLE`) until the user chooses a CV again.
 */
export type ConnectedAppCvStatus = 'available' | 'unavailable';

export interface ConnectedAppCvSummary {
  /**
   * Null when the connection follows the user's main CV automatically. When
   * `status` is `'unavailable'` this is the id of the deleted CV.
   */
  id: string | null;
  title: string;
  isMainCv: boolean;
  /**
   * Optional and additive: an API older than this field sends none, which
   * means available.
   */
  status?: ConnectedAppCvStatus;
  selection?: import('./cv-release').CvDocumentSelection;
  edition?: import('./cv-release').CvSelectedDocumentIdentity;
}

export interface ConnectedAppSummary {
  id: string;
  app: DeveloperAppPublicInfo;
  grantedScopes: UserScope[];
  /** Scopes the user may toggle on again (requested by the app at consent). */
  availableScopes: UserScope[];
  cv: ConnectedAppCvSummary | null;
  shareAvatar: boolean;
  createdAt: string;
  lastUsedAt: string | null;
}

export interface UpdateConnectedAppInput {
  cvId?: string | null;
  cvSelection?: import('./cv-release').CvDocumentSelection | null;
  shareAvatar?: boolean;
  grantedScopes?: UserScope[];
}

// ---------------------------------------------------------------------------
// Consent screen contract (/api/oauth/authorize/*)
// ---------------------------------------------------------------------------

export interface OAuthAuthorizeRequestParams {
  responseType: string;
  clientId: string;
  redirectUri: string;
  scope: string;
  state?: string | null;
  codeChallenge: string;
  codeChallengeMethod: string;
}

export interface OAuthConsentCvOption {
  /** "master" for the profile-backed main CV, otherwise the CV id. */
  id: string;
  title: string;
  isMainCv: boolean;
  updatedAt: string;
}

export interface OAuthConsentContext {
  app: DeveloperAppPublicInfo;
  requestedScopes: UserScope[];
  scopeDetails: Array<{ scope: UserScope; required: boolean }>;
  redirectUri: string;
  existingConnection: {
    id: string;
    grantedScopes: UserScope[];
    cvId: string | null;
    cv?: ConnectedAppCvSummary;
    shareAvatar: boolean;
  } | null;
  user: {
    id: string;
    name: string;
    email: string;
    avatarUrl: string | null;
  };
  cvs: OAuthConsentCvOption[];
}

export interface OAuthApproveInput extends OAuthAuthorizeRequestParams {
  grantedScopes: UserScope[];
  cvId: string | null;
  cvSelection?: import('./cv-release').CvDocumentSelection | null;
  shareAvatar: boolean;
}

export interface OAuthRedirectResult {
  redirectUrl: string;
}

// ---------------------------------------------------------------------------
// Developer platform app (platform.tabbio.com): accounts, usage, logs, limits
// See docs/planning/developer-platform-app-plan.md.
// ---------------------------------------------------------------------------

export const DEVELOPER_PLANS = ['standard', 'elevated', 'custom'] as const;
export type DeveloperPlan = (typeof DEVELOPER_PLANS)[number];

export const DEVELOPER_ACCOUNT_STATUSES = ['active', 'suspended'] as const;
export type DeveloperAccountStatus = (typeof DEVELOPER_ACCOUNT_STATUSES)[number];

export interface DeveloperLimitValues {
  /** Requests per rolling minute across every app the account owns. */
  rateLimitPerMinute: number;
  /** Requests per UTC day across every app the account owns. */
  dailyRequestLimit: number;
}

/** Defaults per plan. `custom` has no defaults: the account row carries both values. */
export const DEVELOPER_PLAN_LIMITS: Record<
  Exclude<DeveloperPlan, 'custom'>,
  DeveloperLimitValues
> = {
  standard: { rateLimitPerMinute: 300, dailyRequestLimit: 20_000 },
  elevated: { rateLimitPerMinute: 1_000, dailyRequestLimit: 200_000 },
};

export const DEVELOPER_API_KEY_LIMIT_PER_APP = 10;

/** How a request to /v1 or /oauth ended, classified from the HTTP status. */
export const REQUEST_OUTCOMES = [
  'success',
  'client_error',
  'auth_error',
  'rate_limited',
  'server_error',
] as const;
export type RequestOutcome = (typeof REQUEST_OUTCOMES)[number];

/** Which credential authenticated a recorded request. */
export const REQUEST_CREDENTIAL_KINDS = [
  'api_key',
  'access_token',
  'client',
  'none',
] as const;
export type RequestCredentialKind = (typeof REQUEST_CREDENTIAL_KINDS)[number];

export const USAGE_RANGES = ['24h', '7d', '30d', '90d'] as const;
export type UsageRange = (typeof USAGE_RANGES)[number];

export const USAGE_INTERVALS = ['hour', 'day'] as const;
export type UsageInterval = (typeof USAGE_INTERVALS)[number];

export const USAGE_GROUP_KEYS = ['outcome', 'app', 'route', 'credential'] as const;
export type UsageGroupKey = (typeof USAGE_GROUP_KEYS)[number];

export const USAGE_BREAKDOWN_KEYS = ['app', 'route', 'credential', 'status'] as const;
export type UsageBreakdownKey = (typeof USAGE_BREAKDOWN_KEYS)[number];

/** Upper edges (ms) of the latency histogram bins stored per usage bucket. */
export const LATENCY_BIN_EDGES_MS = [100, 250, 500, 1000, 2500] as const;

export const DEVELOPER_USAGE_RETENTION_DAYS = {
  requestLog: 30,
  usageBucket: 400,
  usageDay: 90,
} as const;

export interface DeveloperAccount {
  id: string;
  userId: string;
  status: DeveloperAccountStatus;
  plan: DeveloperPlan;
  displayName: string;
  organizationName: string | null;
  websiteUrl: string | null;
  contactEmail: string | null;
  /** Effective limits after plan defaults and per-account overrides. */
  limits: DeveloperLimitValues;
  overrides: {
    rateLimitPerMinute: number | null;
    dailyRequestLimit: number | null;
  };
  suspendedAt: string | null;
  suspendedReason: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface CreateDeveloperAccountInput {
  displayName?: string;
}

export interface UpdateDeveloperAccountInput {
  displayName?: string;
  organizationName?: string | null;
  websiteUrl?: string | null;
  contactEmail?: string | null;
}

export interface DeveloperUsageTotals {
  requests: number;
  success: number;
  clientError: number;
  authError: number;
  rateLimited: number;
  serverError: number;
  /** 0..1, null when there were no requests. */
  successRate: number | null;
  p50Ms: number | null;
  p95Ms: number | null;
}

export interface DeveloperUsageSummary {
  range: UsageRange;
  from: string;
  to: string;
  current: DeveloperUsageTotals;
  previous: DeveloperUsageTotals;
  activeConnections: number;
  appCount: number;
}

export interface DeveloperUsagePoint {
  /** Bucket start, ISO 8601 UTC. */
  bucket: string;
  series: Record<string, number>;
}

export interface DeveloperUsageTimeseries {
  range: UsageRange;
  interval: UsageInterval;
  groupBy: UsageGroupKey;
  from: string;
  to: string;
  /** Series keys in display order, with a human label for each. */
  keys: { key: string; label: string }[];
  points: DeveloperUsagePoint[];
}

export interface DeveloperUsageBreakdownRow {
  key: string;
  label: string;
  requests: number;
  /** 0..1 share of the total requests in the range. */
  share: number;
  successRate: number | null;
  p50Ms: number | null;
  p95Ms: number | null;
}

export interface DeveloperUsageBreakdown {
  range: UsageRange;
  by: UsageBreakdownKey;
  from: string;
  to: string;
  totalRequests: number;
  rows: DeveloperUsageBreakdownRow[];
}

export interface DeveloperRequestLogEntry {
  id: string;
  appId: string | null;
  appName: string | null;
  credentialKind: RequestCredentialKind;
  credentialId: string | null;
  /** Key name or token prefix, resolved for display; null when unknown. */
  credentialLabel: string | null;
  connectionId: string | null;
  method: string;
  route: string;
  statusCode: number;
  outcome: RequestOutcome;
  errorCode: string | null;
  durationMs: number;
  userAgent: string | null;
  occurredAt: string;
}

export interface DeveloperRequestLogPage {
  entries: DeveloperRequestLogEntry[];
  nextCursor: string | null;
}

export interface DeveloperLimitWindow {
  /** null when the live counter is unavailable (per-minute window on memory storage). */
  used: number | null;
  limit: number;
  resetsAt: string;
}

export interface DeveloperStaticTier {
  label: string;
  path: string;
  limit: number;
  windowSeconds: number;
}

export interface DeveloperLimits {
  plan: DeveloperPlan;
  limits: DeveloperLimitValues;
  minute: DeveloperLimitWindow;
  day: DeveloperLimitWindow;
  tiers: DeveloperStaticTier[];
}

/** The developer's view of a user who connected to one of their apps. */
export interface DeveloperAppConnection {
  id: string;
  status: DeveloperConnectionStatus;
  grantedScopes: UserScope[];
  cvShared: boolean;
  shareAvatar: boolean;
  /** Only present when the `profile` scope was granted. */
  userDisplayName: string | null;
  createdAt: string;
  lastUsedAt: string | null;
  revokedAt: string | null;
}

export interface DeveloperAppConnectionPage {
  connections: DeveloperAppConnection[];
  nextCursor: string | null;
}

export interface DeveloperApiKeyListItem extends DeveloperApiKeySummary {
  appId: string;
  appName: string;
  requests7d: number;
}

export interface UpdateDeveloperApiKeyInput {
  name: string;
}

export interface DeveloperOverview {
  account: DeveloperAccount;
  summary: DeveloperUsageSummary;
  limits: DeveloperLimits;
  series: DeveloperUsageTimeseries;
  topRoutes: DeveloperUsageBreakdownRow[];
  recentFailures: DeveloperRequestLogEntry[];
  apps: DeveloperAppSummary[];
}
